/**
 * M15: Public campaigns listing at /campaign (subdomain or /:orgSlug/campaign).
 * NOS-265: org/nav/customizations shell via usePublicSiteShell + PublicSitePage —
 * fixes the parent-supplied customizations being overwritten by a redundant
 * fetch, and the nav bar missing getPagePath/orgId/stickyNav here.
 */

import { useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"
import { PublicSitePage } from "@/components/PublicSitePage"
import { SEOHead } from "@/components/public/SEOHead"
import { getPublicSiteOrigin } from "@/lib/publicSiteUrl"
import { usePublicSiteShell } from "@/hooks/usePublicSiteShell"
import type { Organization, OrgCustomizations } from "@/templates/types"
import { CampaignCard } from "@/components/public/CampaignCard"
import type { Tables } from "@/types/database"
import { campaignEnded, daysRemaining } from "@/lib/campaigns/campaignDates"

type CampaignRow = Tables<"campaigns">

interface CampaignListingPageProps {
  orgSlug: string
  org?: Organization | null
  customizations?: OrgCustomizations | null
  basePath?: string
}

async function fetchPublicTotals(campaignId: string): Promise<{ raised_cents: number; donor_count: number }> {
  const { data, error } = await supabase.rpc("get_campaign_public_totals", { cid: campaignId })
  if (error || !data?.length) return { raised_cents: 0, donor_count: 0 }
  const row = data[0] as { raised_cents: number; donor_count: number }
  return { raised_cents: Number(row.raised_cents), donor_count: Number(row.donor_count) }
}

export function CampaignListingPage({
  orgSlug,
  org: orgProp,
  customizations: custProp,
  basePath = "",
}: CampaignListingPageProps) {
  const shell = usePublicSiteShell(orgSlug, {
    org: orgProp,
    customizations: custProp,
    basePath,
  })
  const { org, primaryColor } = shell

  const [campaigns, setCampaigns] = useState<CampaignRow[]>([])
  const [raisedById, setRaisedById] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)

  const orgId = org?.id
  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!orgId) return

      const { data: campRows, error: campErr } = await supabase
        .from("campaigns")
        .select("*")
        .eq("org_id", orgId)
        .eq("is_published", true)
        .order("start_date", { ascending: false, nullsFirst: false })

      if (cancelled) return
      if (campErr) {
        setCampaigns([])
        setRaisedById({})
      } else {
        const list = (campRows ?? []) as CampaignRow[]
        setCampaigns(list)
        const totals = await Promise.all(list.map((c) => fetchPublicTotals(c.id)))
        const map: Record<string, number> = {}
        list.forEach((c, i) => {
          map[c.id] = totals[i]?.raised_cents ?? 0
        })
        setRaisedById(map)
      }
      setLoading(false)
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [orgId])

  if (loading || shell.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    )
  }

  if (shell.notFound || !org) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <p className="text-muted-foreground">Organization not found.</p>
      </div>
    )
  }

  const origin = getPublicSiteOrigin(org)
  const desc = `Support ${org.name}'s fundraising campaigns`
  const jsonLd =
    campaigns.length > 0
      ? [
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: `${org.name} campaigns`,
            numberOfItems: campaigns.length,
            itemListElement: campaigns.map((c, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: c.title,
              url: `${origin}/campaign/${c.slug}`,
            })),
          },
        ]
      : []

  return (
    <PublicSitePage
      shell={shell}
      className="bg-gray-50"
      head={
        <SEOHead
          title={`${org.name} — Campaigns`}
          description={desc}
          canonicalUrl={`${origin}/campaign`}
          ogType="website"
          imageUrl={org.logo_url}
          jsonLd={jsonLd}
        />
      }
    >
      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold tracking-tight">Campaigns</h1>
        <p className="mt-2 text-muted-foreground">{desc}</p>

        {campaigns.length === 0 ? (
          <p className="mt-12 text-center text-muted-foreground" data-testid="campaign-listing-empty">
            No active campaigns at this time
          </p>
        ) : (
          <div className="mt-8 grid gap-6 sm:grid-cols-2">
            {campaigns.map((c) => (
              <CampaignCard
                key={c.id}
                campaign={c}
                raisedCents={raisedById[c.id] ?? 0}
                basePath={basePath}
                primaryColor={primaryColor}
                daysRemaining={daysRemaining(c.end_date)}
                ended={campaignEnded(c.end_date)}
              />
            ))}
          </div>
        )}
      </main>
    </PublicSitePage>
  )
}
